import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ICurrencyAmountConfig, ICurrencyAmountOption } from '../../../../framework/models/form-template';
import { FeatureFlagsService } from '../../../services/feature-flags.service';

interface ICurrencyAmountViewModel {
  code: string;
  symbol: string;
  label: string;
  dropdownLabel: string;
}

@Component({
  selector: 'app-currency-amount-input',
  standalone: false,
  templateUrl: './currency-amount-input.component.html',
  styleUrls: ['./currency-amount-input.component.scss']
})
export class CurrencyAmountInputComponent implements OnChanges {
  @Input() config?: ICurrencyAmountConfig;
  @Input() amount: number | null = null;
  @Input() currency: string;
  @Input() isEditable: boolean = true;

  @Output() valueChange = new EventEmitter<{ amount: number | null; currency: string }>();

  /** Stable cached reference — only rebuilt when config input changes. */
  currencyOptions: ICurrencyAmountViewModel[] = this.buildCurrencyOptions();

  constructor(private readonly featureFlagsService: FeatureFlagsService) {
    // Field initializers run before DI assignment, so the region-aware default is set here instead.
    this.currency = this.featureFlagsService.getDefaultCurrency();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.currencyOptions = this.buildCurrencyOptions();
    }
  }

  private buildCurrencyOptions(): ICurrencyAmountViewModel[] {
    const configuredOptions = this.config?.currencies;
    if (!configuredOptions || configuredOptions.length === 0) {
      return this.normalizeCurrencies([
        { code: 'USD', symbol: '$', label: 'USD' },
        { code: 'EUR', symbol: '€', label: 'EUR' },
        { code: 'GBP', symbol: '£', label: 'GBP' }
      ]);
    }
    return this.normalizeCurrencies(configuredOptions);
  }

  get selectedCurrencySymbol(): string {
    return this.getSelectedCurrencyOption()?.symbol ?? '$';
  }

  get minAmount(): number {
    return this.config?.minAmount ?? this.config?.validationRules?.minAmount ?? 0;
  }

  get amountFormat(): string {
    return `${this.selectedCurrencySymbol} #,##0.00`;
  }

  onCurrencySelected(e: any): void {
    const nextCurrency = e?.value ?? this.currency ?? this.config?.defaultCurrency ?? this.featureFlagsService.getDefaultCurrency();
    this.emitValue(this.amount, nextCurrency);
  }

  onAmountChanged(e: any): void {
    const nextAmount = e?.value ?? null;
    this.emitValue(nextAmount, this.currency || this.config?.defaultCurrency || this.featureFlagsService.getDefaultCurrency());
  }

  private emitValue(amount: number | null, currency: string): void {
    this.valueChange.emit({ amount, currency });
  }

  private getSelectedCurrencyOption(): ICurrencyAmountViewModel | undefined {
    const requestedCode = this.currency || this.config?.defaultCurrency || this.featureFlagsService.getDefaultCurrency();
    return this.currencyOptions.find(option => option.code === requestedCode) ?? this.currencyOptions[0];
  }

  private normalizeCurrencies(options: ICurrencyAmountOption[]): ICurrencyAmountViewModel[] {
    return options.map(option => {
      const symbol = option.symbol ?? this.getDefaultSymbol(option.code);
      const label = option.label ?? option.code;
      return {
        code: option.code,
        symbol,
        label,
        dropdownLabel: `${symbol} ${label}`
      };
    });
  }

  private getDefaultSymbol(currencyCode: string): string {
    switch (currencyCode?.toUpperCase()) {
      case 'EUR':
        return '€';
      case 'GBP':
        return '£';
      case 'USD':
      default:
        return '$';
    }
  }
}